import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import { useToast } from '../../context/ToastContext';
import Header from '../../components/common/Header';
import Sidebar from '../../components/common/Sidebar';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { Building2, Plus, Edit, X, Users, Image as ImageIcon, Bot, Sparkles } from 'lucide-react';
import './AdminDepartments.css';

const AdminDepartments = () => {
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingDept, setEditingDept] = useState(null);

  const [formData, setFormData] = useState({ name: '', description: '', is_active: true });
  const [deptImageFile, setDeptImageFile] = useState(null);
  const [avatarImageFile, setAvatarImageFile] = useState(null);
  const [deptImagePreview, setDeptImagePreview] = useState(null);
  const [avatarImagePreview, setAvatarImagePreview] = useState(null);

  const { addToast } = useToast();

  useEffect(() => {
    fetchDepartments();
  }, []);

  const fetchDepartments = async () => {
    try {
      setLoading(true);
      const res = await api.get('/departments/');
      setDepartments(res.data.results || res.data);
    } catch (err) {
      console.error(err);
      addToast('Failed to load departments.', 'error');
    } finally {
      setLoading(false);
    }
  };

  const openCreateModal = () => {
    setEditingDept(null);
    setFormData({ name: '', description: '', is_active: true });
    setDeptImageFile(null);
    setAvatarImageFile(null);
    setDeptImagePreview(null);
    setAvatarImagePreview(null);
    setShowModal(true);
  };

  const handleEdit = (dept) => {
    setEditingDept(dept);
    setFormData({ name: dept.name, description: dept.description || '', is_active: dept.is_active });
    setDeptImageFile(null);
    setAvatarImageFile(null);
    setDeptImagePreview(dept.department_image || null);
    setAvatarImagePreview(dept.avatar_image || null);
    setShowModal(true);
  };

  const handleDeptImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setDeptImageFile(file);
      setDeptImagePreview(URL.createObjectURL(file));
    }
  };

  const handleAvatarImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setAvatarImageFile(file);
      setAvatarImagePreview(URL.createObjectURL(file));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      const submitData = new FormData();
      submitData.append('name', formData.name);
      submitData.append('description', formData.description);
      submitData.append('is_active', formData.is_active);

      if (deptImageFile) {
        submitData.append('department_image', deptImageFile);
      }
      if (avatarImageFile) {
        submitData.append('avatar_image', avatarImageFile);
      }

      const headers = { 'Content-Type': 'multipart/form-data' };

      if (editingDept) {
        await api.patch(`/departments/${editingDept.id}/`, submitData, { headers });
        addToast('Department updated successfully!', 'success');
      } else {
        await api.post('/departments/', submitData, { headers });
        addToast('Department created successfully!', 'success');
      }

      setShowModal(false);
      fetchDepartments();
    } catch (err) {
      console.error(err);
      addToast('Failed to save department.', 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="page-layout-root">
      <Header />
      <div className="page-body-wrapper">
        <Sidebar />
        <main className="page-main-content">
          
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '28px' }}>
            <div>
              <h1 style={{ fontSize: '1.8rem', fontWeight: 800 }}>Department Management</h1>
              <p style={{ color: '#64748b' }}>Configure organizational units, department banners, and assigned AI Trainer avatars.</p>
            </div>
            <button className="btn btn-primary" onClick={openCreateModal}>
              <Plus size={18} /> Create Department
            </button>
          </div>

          {loading ? <LoadingSpinner label="Loading departments..." /> : (
            <div className="admin-dept-grid">
              {departments.map((dept) => (
                <div key={dept.id} className="card admin-dept-card">
                  <div>
                    {/* Department Cover Banner */}
                    <div className="admin-dept-cover-banner">
                      {dept.department_image ? (
                        <img
                          src={dept.department_image}
                          alt={`${dept.name} Cover`}
                          className="admin-dept-cover-img"
                        />
                      ) : (
                        <div className="admin-dept-cover-gradient" />
                      )}

                      <div className="admin-dept-tag">
                        <Building2 size={14} /> Department
                      </div>

                      <span className={`badge ${dept.is_active ? 'badge-success' : 'badge-warning'} admin-dept-badge-shadow`}>
                        {dept.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </div>

                    {/* AI Trainer Avatar Badge & Content */}
                    <div className="admin-dept-body">
                      <div className="admin-dept-avatar-row">
                        {/* Avatar container */}
                        <div className="admin-dept-avatar-box">
                          {dept.avatar_image ? (
                            <img src={dept.avatar_image} alt={`${dept.name} Avatar`} className="admin-dept-avatar-img" />
                          ) : (
                            <div className="admin-dept-avatar-fallback">
                              <Bot size={32} />
                            </div>
                          )}
                        </div>

                        <div className="admin-dept-assigned-tag">
                          <Sparkles size={12} /> AI Trainer Assigned
                        </div>
                      </div>

                      <h3 className="admin-dept-name">{dept.name}</h3>
                      <p className="admin-dept-desc">
                        {dept.description || 'No description provided.'}
                      </p>
                    </div>
                  </div>

                  <div className="admin-dept-footer">
                    <span className="admin-dept-emp-count">
                      <Users size={16} /> {dept.employee_count || 0} Employees
                    </span>
                    <button className="btn btn-secondary btn-sm" onClick={() => handleEdit(dept)}>
                      <Edit size={14} /> Edit Department
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Create / Edit Department Modal */}
          {showModal && (
            <div className="modal-backdrop">
              <div className="modal-content" style={{ padding: '32px', maxWidth: '560px', width: '90%' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px' }}>
                  <h2 style={{ fontSize: '1.3rem', fontWeight: 800 }}>{editingDept ? 'Edit Department' : 'Create New Department'}</h2>
                  <button onClick={() => setShowModal(false)}><X size={20} /></button>
                </div>

                <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '18px' }}>
                  
                  {/* Department Name */}
                  <div className="form-group" style={{ margin: 0 }}>
                    <label className="form-label">Department Name *</label>
                    <input
                      type="text"
                      className="form-input"
                      required
                      placeholder="e.g. Software Engineering, HR & Legal..."
                      value={formData.name}
                      onChange={e => setFormData({ ...formData, name: e.target.value })}
                    />
                  </div>

                  {/* Description */}
                  <div className="form-group" style={{ margin: 0 }}>
                    <label className="form-label">Description</label>
                    <textarea
                      className="form-textarea"
                      rows={3}
                      placeholder="Overview of this department's role and responsibilities..."
                      value={formData.description}
                      onChange={e => setFormData({ ...formData, description: e.target.value })}
                    />
                  </div>

                  {/* Department Cover Image File */}
                  <div className="form-group" style={{ margin: 0 }}>
                    <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <ImageIcon size={16} style={{ color: '#4f46e5' }} /> Department Cover Image
                    </label>
                    <div className="admin-dept-file-row">
                      {deptImagePreview && (
                        <div className="admin-dept-preview-cover">
                          <img src={deptImagePreview} alt="Cover Preview" className="admin-dept-preview-img" />
                        </div>
                      )}
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleDeptImageChange}
                        style={{ fontSize: '0.85rem' }}
                      />
                    </div>
                  </div>

                  {/* AI Trainer Avatar Image File */}
                  <div className="form-group" style={{ margin: 0 }}>
                    <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <Bot size={16} style={{ color: '#059669' }} /> Department AI Trainer / Lead Avatar Image
                    </label>
                    <div className="admin-dept-file-row">
                      {avatarImagePreview && (
                        <div className="admin-dept-preview-avatar">
                          <img src={avatarImagePreview} alt="Avatar Preview" className="admin-dept-preview-img" />
                        </div>
                      )}
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleAvatarImageChange}
                        style={{ fontSize: '0.85rem' }}
                      />
                    </div>
                    <span className="admin-dept-help-text">
                      This avatar will represent the AI Lead Assisting employees of this department across all portal pages.
                    </span>
                  </div>

                  {/* Active Toggle */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <input
                      type="checkbox"
                      id="is_active_cb"
                      checked={formData.is_active}
                      onChange={e => setFormData({ ...formData, is_active: e.target.checked })}
                      style={{ width: '16px', height: '16px', cursor: 'pointer' }}
                    />
                    <label htmlFor="is_active_cb" style={{ fontSize: '0.9rem', fontWeight: 600, color: 'var(--text-main, #f8fafc)', cursor: 'pointer' }}>
                      Active Department
                    </label>
                  </div>

                  {/* Form Action Buttons */}
                  <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '12px' }}>
                    <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)} disabled={saving}>
                      Cancel
                    </button>
                    <button type="submit" className="btn btn-primary" disabled={saving}>
                      {saving ? 'Saving...' : 'Save Department'}
                    </button>
                  </div>

                </form>
              </div>
            </div>
          )}

        </main>
      </div>
    </div>
  );
};

export default AdminDepartments;
